import { ApiError, requestJson as defaultRequestJson } from "./api.js";

function createDialog(document) {
  const dialog = document.createElement("div");
  dialog.id = "accountRatingDialog";
  dialog.className = "account-dialog rating-dialog";
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");
  dialog.setAttribute("aria-labelledby", "accountRatingTitle");
  dialog.hidden = true;

  const panel = document.createElement("div");
  panel.className = "account-dialog-panel";
  const title = document.createElement("h2");
  title.id = "accountRatingTitle";
  title.textContent = "Rate bottle";
  const options = document.createElement("fieldset");
  options.className = "rating-options";
  const legend = document.createElement("legend");
  legend.textContent = "Your rating out of 10";
  options.append(legend);
  for (let score = 10; score >= 1; score -= 1) {
    const label = document.createElement("label");
    const input = document.createElement("input");
    input.type = "radio";
    input.name = "account-rating";
    input.value = String(score);
    label.append(input, document.createTextNode(String(score)));
    options.append(label);
  }
  const error = document.createElement("p");
  error.id = "accountRatingError";
  error.className = "account-error";
  error.setAttribute("role", "alert");
  const actions = document.createElement("div");
  actions.className = "account-dialog-actions";
  const retry = document.createElement("button");
  retry.type = "button";
  retry.id = "retryAccountRating";
  retry.textContent = "Retry";
  retry.hidden = true;
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.textContent = "Cancel";
  const save = document.createElement("button");
  save.type = "button";
  save.id = "saveAccountRating";
  save.textContent = "Save rating";
  actions.append(retry, cancel, save);
  panel.append(title, options, error, actions);
  dialog.append(panel);
  document.body.append(dialog);
  return { dialog, title, error, retry, cancel, save };
}

export function createRatingsController({
  document,
  auth,
  requestJson = defaultRequestJson,
  onSave = () => {},
}) {
  const controls = new Map();
  const summaries = new Map();
  const loaded = new Set();
  const loading = new Map();
  const registered = new WeakSet();
  const modal = createDialog(document);
  let activeBottle = null;
  let observer = null;

  function render(code) {
    const summary = summaries.get(code) ?? { avg: null, count: 0, yourRating: null };
    for (const control of controls.get(code) ?? []) {
      if (summary.count) {
        control.summary.textContent = `${summary.avg} · ${summary.count}`;
        control.summary.setAttribute(
          "aria-label",
          `Average rating ${summary.avg} from ${summary.count} ${summary.count === 1 ? "rating" : "ratings"}`,
        );
      } else {
        control.summary.textContent = "—";
        control.summary.setAttribute("aria-label", "No ratings yet");
      }
      control.action.textContent = summary.yourRating == null
        ? "Rate"
        : `Rated ${summary.yourRating}`;
    }
  }

  function registerControl(code, container) {
    if (!code || registered.has(container)) return;
    const summary = container.querySelector(".rating-summary");
    const action = container.querySelector("[data-rate-bottle]");
    if (!summary || !action) return;
    registered.add(container);
    const list = controls.get(code) ?? [];
    list.push({ container, summary, action });
    controls.set(code, list);
    render(code);
  }

  async function refreshBottle(code) {
    if (loading.has(code)) return loading.get(code);
    const task = requestJson(`/api/ratings?bottle=${encodeURIComponent(code)}`, { auth })
      .then((summary) => {
        summaries.set(code, summary);
        loaded.add(code);
        render(code);
        return summary;
      })
      .catch((error) => {
        for (const control of controls.get(code) ?? []) {
          control.summary.textContent = "—";
          control.summary.setAttribute("aria-label", "Rating unavailable");
        }
        throw error;
      })
      .finally(() => loading.delete(code));
    loading.set(code, task);
    return task;
  }

  function closeModal() {
    modal.dialog.hidden = true;
    activeBottle = null;
  }

  function showModal(code) {
    activeBottle = code;
    const personal = summaries.get(code)?.yourRating;
    modal.title.textContent = `Rate ${code}`;
    modal.error.textContent = "";
    modal.retry.hidden = true;
    const selected = modal.dialog.querySelector(`input[value="${personal ?? 10}"]`);
    if (selected) selected.checked = true;
    modal.dialog.hidden = false;
    selected?.focus();
  }

  async function open(code) {
    if (!auth.isSignedIn()) {
      await auth.requireUser(() => showModal(code));
      return;
    }
    showModal(code);
  }

  async function setRating(code, rating, { silent = false } = {}) {
    const previousRating = summaries.get(code)?.yourRating ?? null;
    const summary = await requestJson(`/api/ratings/${encodeURIComponent(code)}`, {
      method: "PUT",
      body: { rating },
      auth,
    });
    summaries.set(code, summary);
    loaded.add(code);
    render(code);
    const result = { code, previousRating, summary };
    if (!silent) onSave(result);
    return result;
  }

  async function saveRating() {
    const code = activeBottle;
    if (!code) return;
    const checked = modal.dialog.querySelector('input[name="account-rating"]:checked');
    if (!checked) return;
    modal.save.disabled = true;
    modal.retry.hidden = true;
    modal.error.textContent = "";
    try {
      await setRating(code, Number(checked.value));
      closeModal();
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        modal.error.textContent = "Please sign in again to save this rating.";
        await auth.requireUser(undefined, { force: true });
      } else {
        modal.error.textContent = "The rating could not be saved.";
        modal.retry.hidden = false;
      }
    } finally {
      modal.save.disabled = false;
    }
  }

  function ratingSlots(root) {
    const slots = [...root.querySelectorAll("[data-rating-bottle]")];
    if (root.matches?.("[data-rating-bottle]")) slots.unshift(root);
    return slots;
  }

  async function mount(root = document) {
    const pendingLoads = [];
    for (const slot of ratingSlots(root)) {
      const code = slot.dataset.ratingBottle;
      registerControl(code, slot);
      if (loaded.has(code) || loading.has(code)) continue;
      if (typeof globalThis.IntersectionObserver === "function") {
        if (!observer) {
          observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              observer.unobserve(entry.target);
              refreshBottle(entry.target.dataset.ratingBottle).catch(() => {});
            }
          }, { rootMargin: "300px" });
        }
        observer.observe(slot);
      } else {
        pendingLoads.push(refreshBottle(code));
      }
    }
    await Promise.all(pendingLoads);
  }

  modal.cancel.addEventListener("click", closeModal);
  modal.retry.addEventListener("click", saveRating);
  modal.save.addEventListener("click", saveRating);
  modal.dialog.addEventListener("click", (event) => {
    if (event.target === modal.dialog) closeModal();
  });
  document.addEventListener("keydown", (event) => {
    if (!modal.dialog.hidden && event.key === "Escape") closeModal();
  });
  if (document.__smwsRatingClickHandler) {
    document.removeEventListener("click", document.__smwsRatingClickHandler);
  }
  const handleRatingClick = (event) => {
    const action = event.target.closest?.("[data-rate-bottle]");
    if (!action) return;
    open(action.dataset.rateBottle);
  };
  document.__smwsRatingClickHandler = handleRatingClick;
  document.addEventListener("click", handleRatingClick);

  return {
    async start() {
      auth.subscribe?.(({ signedIn }) => {
        if (!signedIn) this.clearPrivateState();
      });
      await mount(document);
    },

    mount,
    setRating,
    refreshBottle,
    clearPrivateState() {
      for (const [code, summary] of summaries) {
        summaries.set(code, { ...summary, yourRating: null });
        render(code);
      }
      if (!modal.dialog.hidden) closeModal();
    },
  };
}
